import { useMemo, useState } from 'react';
import { Copy, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEmojiDirectory } from '@/components/EmojiDisplay';
import { cn } from '@/lib/utils';

/**
 * Global emoji browser — every custom emoji from every guild the bot is in,
 * plus copy-to-clipboard for both the plain identifier ("name:id") and the
 * chat form ("<:name:id>" / "<a:name:id>"), so users can paste into embed
 * descriptions or chat.
 */

export function EmojiExplorer() {
  const dir = useEmojiDirectory();
  const [query, setQuery] = useState('');
  const [guildFilter, setGuildFilter] = useState<string>('all');
  const [showAnimated, setShowAnimated] = useState<'all' | 'static' | 'animated'>('all');
  const [justCopied, setJustCopied] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (dir.data?.emojis ?? []).filter((e) => {
      if (guildFilter !== 'all' && e.guildId !== guildFilter) return false;
      if (showAnimated === 'animated' && !e.animated) return false;
      if (showAnimated === 'static' && e.animated) return false;
      if (!q) return true;
      return e.name.toLowerCase().includes(q);
    });
  }, [dir.data, query, guildFilter, showAnimated]);

  const copy = (value: string) => {
    navigator.clipboard.writeText(value);
    setJustCopied(value);
    setTimeout(() => setJustCopied((v) => (v === value ? null : v)), 1500);
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-4 flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Emoji-Übersicht</h1>
        {dir.data && (
          <span className="text-xs text-muted-foreground">
            {dir.data.emojis.length} Emojis aus {dir.data.guilds.length} Servern
          </span>
        )}
      </div>

      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <div className="min-w-[240px] flex-1">
            <Label>Suche</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Emoji-Name"
                className="pl-8"
              />
            </div>
          </div>
          <div>
            <Label>Typ</Label>
            <div className="flex gap-1">
              {(
                [
                  ['all', 'Alle'],
                  ['static', 'Statisch'],
                  ['animated', 'Animiert'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setShowAnimated(key)}
                  className={cn(
                    'rounded-md border px-3 py-1.5 text-xs',
                    showAnimated === key
                      ? 'border-primary bg-primary/15 text-primary'
                      : 'bg-background hover:bg-accent'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => setGuildFilter('all')}
              className={cn(
                'rounded-full border px-3 py-1 text-xs',
                guildFilter === 'all'
                  ? 'border-primary bg-primary/15 text-primary'
                  : 'hover:bg-accent'
              )}
            >
              Alle Server ({dir.data?.emojis.length ?? 0})
            </button>
            {(dir.data?.guilds ?? []).map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setGuildFilter(g.id)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs',
                  guildFilter === g.id
                    ? 'border-primary bg-primary/15 text-primary'
                    : 'hover:bg-accent'
                )}
              >
                {g.iconUrl && (
                  <img src={g.iconUrl} alt="" className="h-4 w-4 rounded-full" />
                )}
                {g.name} <span className="opacity-60">({g.emojiCount})</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {dir.isLoading && <p className="text-muted-foreground">Lade…</p>}
      {dir.error && (
        <p className="text-sm text-destructive">
          {(dir.error as Error).message}
        </p>
      )}

      <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2">
        {filtered.map((e) => {
          const chat = `<${e.animated ? 'a' : ''}:${e.name}:${e.id}>`;
          const copiedIdentifier = justCopied === e.identifier;
          const copiedChat = justCopied === chat;
          return (
            <Card key={e.id} className="flex flex-col items-center p-3 text-center">
              <img
                src={e.imageUrl}
                alt={e.name}
                className="mb-2 h-12 w-12"
                loading="lazy"
              />
              <div className="mb-0.5 w-full truncate text-xs font-medium" title={e.name}>
                :{e.name}:
              </div>
              <div className="mb-2 w-full truncate text-[10px] text-muted-foreground">
                {e.guildName}
              </div>
              <div className="flex w-full gap-1">
                <button
                  type="button"
                  onClick={() => copy(e.identifier)}
                  className={cn(
                    'flex-1 rounded border px-1 py-1 text-[10px]',
                    copiedIdentifier
                      ? 'border-primary text-primary'
                      : 'hover:bg-accent'
                  )}
                  title="Für Config-Felder (name:id)"
                >
                  {copiedIdentifier ? 'kopiert' : <><Copy className="mr-0.5 inline h-3 w-3" />ID</>}
                </button>
                <button
                  type="button"
                  onClick={() => copy(chat)}
                  className={cn(
                    'flex-1 rounded border px-1 py-1 text-[10px]',
                    copiedChat
                      ? 'border-primary text-primary'
                      : 'hover:bg-accent'
                  )}
                  title="Für Chat / Embed-Text (<:name:id>)"
                >
                  {copiedChat ? 'kopiert' : <><Copy className="mr-0.5 inline h-3 w-3" />Chat</>}
                </button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
